import React, { useEffect, useRef, useState } from "react";
import { VersionRuntimeDetails, useAppVersion } from "../lib/version";

function ReleaseNotes({ info, testId = "app-release-notes" }) {
  if (!info?.title && !(info?.highlights || []).length) return null;
  return (
    <div className="mt-2 space-y-2" data-testid={testId}>
      {info.title ? (
        <p className="text-[11px] font-semibold text-slate-300">{info.title}</p>
      ) : null}
      <ul className="list-disc space-y-1 pl-4 text-[11px] leading-snug text-slate-500">
        {(info.highlights || []).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

/** Sidebar footer: one product+runtime block (no duplicate product line). */
export default function VersionPanel() {
  const info = useAppVersion();
  const [notesOpen, setNotesOpen] = useState(false);

  return (
    <div className="min-w-0 border-t border-subtle p-4">
      <VersionRuntimeDetails info={info} testId="sidebar-version-runtime" />
      <button
        type="button"
        data-testid="app-version-toggle"
        onClick={() => setNotesOpen((v) => !v)}
        className="mt-3 w-full text-left font-mono text-[10px] uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-300"
      >
        {notesOpen ? "Release notes verbergen" : "Release notes"}
      </button>
      {notesOpen ? <ReleaseNotes info={info} /> : null}
      <p className="mt-2 font-mono text-[10px] leading-relaxed text-slate-600">
        Prototype · fictional / synthetic data only. Not a certification of compliance.
      </p>
    </div>
  );
}

/**
 * Header control for narrow layouts (sidebar hidden below lg).
 * Same VersionRuntimeDetails as the wide sidebar.
 */
export function VersionInfoMenu({ testId = "version-info" }) {
  const info = useAppVersion();
  const [open, setOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative min-w-0" ref={rootRef}>
      <button
        type="button"
        data-testid={`${testId}-btn`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="rounded-lg border border-subtle px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400 transition-colors hover:border-brand/40 hover:text-slate-200"
      >
        Versie-info
      </button>
      {open ? (
        <div
          data-testid={`${testId}-panel`}
          className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[min(18rem,calc(100vw-1.5rem))] min-w-0 rounded-xl border border-subtle bg-surface/95 p-3 shadow-lg backdrop-blur-xl"
        >
          <VersionRuntimeDetails info={info} testId={`${testId}-details`} />
          <button
            type="button"
            data-testid={`${testId}-notes-toggle`}
            onClick={() => setNotesOpen((v) => !v)}
            className="mt-3 w-full text-left font-mono text-[10px] uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-300"
          >
            {notesOpen ? "Release notes verbergen" : "Release notes"}
          </button>
          {notesOpen ? <ReleaseNotes info={info} testId={`${testId}-notes`} /> : null}
        </div>
      ) : null}
    </div>
  );
}
